import { VFXEffectConfig } from '../../VFXEffectRegistry';

type Primitive = VFXEffectConfig['primitives'][number];

// =============================================================================
// Burst primitive builders — the recurring grammar of one-shot impacts/casts
// =============================================================================
//
// Sibling of statusAuraBuilders (persistent grammar). These are the VERBS the
// F6 wide-dig art queue composes from — every heat-map def (holy trio,
// shadow_cloud, arcane_burst, fire_explosion, slashes, shatters) is a
// combination of:
//   ringBlast    — expanding ring/circle shockwave (the universal impact verb)
//   slashArc     — a fast blade arc (quick/heavy melee reads)
//   shardBurst   — N shards thrown outward from the point (shatter/splinter)
//   glintScatter — small bright sparks scattered + fading (finishing touch)
//   groundCloud  — a lingering low cloud/pool that swells then fades
//   risingColumn — a vertical pillar/beam beat (holy/summon verticality)
//
// Rules the verbs encode so lane agents don't re-learn them:
//  - Multi-tone by default: pass distinct core/edge colors — the play-time
//    palette lock (≥2 distinct fills) then protects the authored look.
//  - Additive-over-dark needs saturation: default alphas run hot.
//  - One-shot tweens only (no repeat: -1) — bursts must END; persistent
//    looks belong to statusAuraBuilders.
// =============================================================================

/** Expanding ring shockwave — the universal impact verb. */
export function ringBlast(opts: {
  radius: number; color: number; alpha?: number; depth?: number; glow?: boolean;
  stroke?: { color: number; width: number };
  expandTo?: number; durationMs?: number; ease?: string;
  /** Stagger the beat — promoted to grammar after three lanes hand-tuned it
   *  (staged double-ring detonations are the core explosion read). */
  delayMs?: number;
}): Primitive {
  const p: Primitive = {
    type: 'circle', radius: opts.radius,
    color: opts.color, alpha: opts.alpha ?? 0.75, depth: opts.depth ?? 94,
    tween: { alpha: 0, scale: opts.expandTo ?? 2.2, duration: opts.durationMs ?? 300, ease: opts.ease ?? 'Power2', ...(opts.delayMs ? { delay: opts.delayMs } : {}) },
  };
  if (opts.glow) p.glow = true;
  if (opts.stroke) p.stroke = opts.stroke;
  return p;
}

/** A fast blade arc — melee slash reads. Angles in degrees, right-facing
 *  basis (ShapeSpawner mirrors on flipX). */
export function slashArc(opts: {
  radius: number; color: number; alpha?: number; depth?: number; glow?: boolean;
  startDeg: number; endDeg: number; width?: number;
  durationMs?: number; fadeDelayMs?: number;
}): Primitive {
  return {
    type: 'arc',
    arcRadius: opts.radius,
    arcStartAngle: opts.startDeg,
    arcEndAngle: opts.endDeg,
    width: opts.width ?? 6,
    color: opts.color, alpha: opts.alpha ?? 0.9, depth: opts.depth ?? 96,
    ...(opts.glow ? { glow: true } : {}),
    tween: { alpha: 0, scale: 1.15, duration: opts.durationMs ?? 240, ease: 'Power2', ...(opts.fadeDelayMs ? { delay: opts.fadeDelayMs } : {}) },
  };
}

/** N shards thrown outward — shatter/splinter verb. */
export function shardBurst(opts: {
  count: number; spread: number; color: number; alpha?: number; depth?: number; glow?: boolean;
  shard?: { points: number[] } | { width: number; height: number };
  travel?: number; durationMs?: number; delayMs?: number; stroke?: { color: number; width: number };
}): Primitive {
  const shape: Partial<Primitive> = opts.shard && 'points' in opts.shard
    ? { type: 'polygon', polygonPoints: opts.shard.points }
    : { type: 'rect', width: (opts.shard as { width: number } | undefined)?.width ?? 6, height: (opts.shard as { height: number } | undefined)?.height ?? 2 };
  const p = {
    ...shape,
    color: opts.color, alpha: opts.alpha ?? 0.9, depth: opts.depth ?? 95,
    scatter: { count: opts.count, radius: opts.spread },
    tween: { alpha: 0, scale: opts.travel ? opts.travel / Math.max(opts.spread, 1) : 1.8, duration: opts.durationMs ?? 320, ease: 'Power2', ...(opts.delayMs ? { delay: opts.delayMs } : {}) },
  } as Primitive;
  if (opts.glow) p.glow = true;
  if (opts.stroke) p.stroke = opts.stroke;
  return p;
}

/** Small bright sparks scattered + fading — the finishing glint. */
export function glintScatter(opts: {
  count: number; spread: number; color?: number; radius?: number; depth?: number;
  durationMs?: number; delayMs?: number;
}): Primitive {
  return {
    type: 'circle', radius: opts.radius ?? 2.5,
    color: opts.color ?? 0xFFFFFF, alpha: 0.95, depth: opts.depth ?? 97, glow: true,
    scatter: { count: opts.count, radius: opts.spread },
    tween: { alpha: 0, scale: 2, duration: opts.durationMs ?? 220, ease: 'Power1', ...(opts.delayMs ? { delay: opts.delayMs } : {}) },
  };
}

/** A lingering low cloud/pool that swells then fades — dot zones, smoke. */
export function groundCloud(opts: {
  width: number; height: number; color: number; alpha?: number; depth?: number;
  offsetY?: number; swellTo?: number; durationMs?: number; stroke?: { color: number; width: number };
}): Primitive {
  const p: Primitive = {
    type: 'ellipse', width: opts.width, height: opts.height,
    offsetY: opts.offsetY ?? 18,
    color: opts.color, alpha: opts.alpha ?? 0.45, depth: opts.depth ?? 93,
    tween: { alpha: 0, scale: opts.swellTo ?? 1.5, duration: opts.durationMs ?? 700, ease: 'Sine.easeOut' },
  };
  if (opts.stroke) p.stroke = opts.stroke;
  return p;
}

/** A vertical pillar beat — holy judgment / summon verticality. */
export function risingColumn(opts: {
  width: number; height: number; color: number; alpha?: number; depth?: number; glow?: boolean;
  riseBy?: number; durationMs?: number;
}): Primitive {
  const p: Primitive = {
    type: 'rect', width: opts.width, height: opts.height,
    offsetY: -(opts.height / 2),
    color: opts.color, alpha: opts.alpha ?? 0.7, depth: opts.depth ?? 95,
    tween: { alpha: 0, y: -(opts.riseBy ?? 24), scale: 1.1, duration: opts.durationMs ?? 380, ease: 'Sine.easeOut' },
  };
  if (opts.glow) p.glow = true;
  return p;
}

// ── Wave-2 verb promotions — each earned by ≥2 hand-rolls in wave 1 ─────────

/** A blade arc + its offset trailing edge — the "weight/glint follow" idiom
 *  (3 wave-1 lanes hand-rolled it: holy_slash, quick_slash, heavy_slash). */
export function shadowedSlash(opts: {
  radius: number; startDeg: number; endDeg: number;
  blade: { color: number; width?: number; alpha?: number; glow?: boolean };
  trail: { color: number; width?: number; alpha?: number; delayMs?: number; radiusOffset?: number };
  durationMs?: number; depth?: number;
}): Primitive[] {
  return [
    slashArc({
      radius: opts.radius, startDeg: opts.startDeg, endDeg: opts.endDeg,
      color: opts.blade.color, width: opts.blade.width, alpha: opts.blade.alpha,
      glow: opts.blade.glow, durationMs: opts.durationMs, depth: opts.depth,
    }),
    slashArc({
      radius: opts.radius + (opts.trail.radiusOffset ?? 4),
      startDeg: opts.startDeg, endDeg: opts.endDeg,
      color: opts.trail.color, width: opts.trail.width ?? 3,
      alpha: opts.trail.alpha ?? 0.4,
      durationMs: opts.durationMs, fadeDelayMs: opts.trail.delayMs ?? 50,
      depth: (opts.depth ?? 96) - 1,
    }),
  ];
}

/** One-shot scattered motes drifting with Sine easing — smoke wisps, rising
 *  embers, leaf puffs (2 wave-1 hand-rolls: warlock wisps, fire embers). The
 *  one-shot sibling of statusAuraBuilders.driftMotes. */
export function wispRise(opts: {
  count: number; spread: number; radius?: number; color: number; alpha?: number;
  direction?: 'up' | 'down'; travel?: number; durationMs?: number; delayMs?: number;
  glow?: boolean; depth?: number;
}): Primitive {
  const p: Primitive = {
    type: 'circle', radius: opts.radius ?? 3,
    color: opts.color, alpha: opts.alpha ?? 0.85, depth: opts.depth ?? 95,
    scatter: { count: opts.count, radius: opts.spread },
    tween: {
      y: (opts.direction ?? 'up') === 'up' ? -(opts.travel ?? 34) : (opts.travel ?? 34),
      alpha: 0, duration: opts.durationMs ?? 650, ease: 'Sine.easeOut',
      ...(opts.delayMs ? { delay: opts.delayMs } : {}),
    },
  };
  if (opts.glow) p.glow = true;
  return p;
}

/** A rotating sharp star pop — the arcane/holy lead-in flash (2 wave-1
 *  hand-rolls: arcane_burst star, HOLY_FLASH). */
export function starFlash(opts: {
  points?: number; innerRadius: number; outerRadius: number; color: number;
  alpha?: number; depth?: number; glow?: boolean;
  spinRad?: number; expandTo?: number; durationMs?: number; delayMs?: number;
}): Primitive {
  const p: Primitive = {
    type: 'star', points: opts.points ?? 5,
    innerRadius: opts.innerRadius, outerRadius: opts.outerRadius,
    color: opts.color, alpha: opts.alpha ?? 0.9, depth: opts.depth ?? 96,
    tween: {
      alpha: 0, scale: opts.expandTo ?? 1.9, rotation: opts.spinRad ?? Math.PI * 0.5,
      duration: opts.durationMs ?? 300, ease: 'Power2',
      ...(opts.delayMs ? { delay: opts.delayMs } : {}),
    },
  };
  if (opts.glow) p.glow = true;
  return p;
}

/** Compound staged detonation — inner flash then outer wave (2 wave-1
 *  hand-rolls: fire_explosion, holy_explosion). */
export function stagedBlast(
  inner: Parameters<typeof ringBlast>[0],
  outer: Parameters<typeof ringBlast>[0],
  gapMs: number,
): Primitive[] {
  return [ringBlast(inner), ringBlast({ ...outer, delayMs: (outer.delayMs ?? 0) + gapMs })];
}
